import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Loader2, TrendingUp } from "lucide-react";

const yen = (n) => "¥" + (Number(n) || 0).toLocaleString("ja-JP");
const thisMonth = () => new Date().toISOString().slice(0, 7);

export default function Wages() {
  const [month, setMonth] = useState(thisMonth());
  const [rows, setRows] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const [f, setF] = useState({
    user_name: "", work_date: new Date().toISOString().slice(0, 10), work_content: "",
    wage_kind: "B型工賃", category: "製造", unit_price: 0, output: 0, amount: 0, deduction: 0,
  });

  const load = useCallback(async (m) => {
    try {
      const [w, r] = await Promise.all([
        api.get(`/wages?month=${m}`),
        api.get(`/wages/report?month=${m}`),
      ]);
      setRows(w.data);
      setReport(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, []);

  useEffect(() => { load(month); }, [month, load]);

  const add = async () => {
    if (!f.user_name || !f.work_date) { toast.error("利用者名と作業日は必須です"); return; }
    setLoading(true);
    try {
      await api.post("/wages", {
        ...f,
        unit_price: Number(f.unit_price) || 0, output: Number(f.output) || 0,
        amount: Number(f.amount) || 0, deduction: Number(f.deduction) || 0,
      });
      toast.success("記録を追加しました");
      setF({ ...f, user_name: "", work_content: "", unit_price: 0, output: 0, amount: 0, deduction: 0 });
      await load(month);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const del = async (id) => {
    try { await api.delete(`/wages/${id}`); await load(month); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-5xl" data-testid="wages-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-bold text-3xl text-stone-900">工賃・賃金台帳</h1>
          <p className="mt-2 text-sm text-stone-500">就労支援事業のA型賃金・B型工賃を記録し、平均工賃月額を自動集計します。</p>
        </div>
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="wages-month" className="w-40" />
      </div>

      {report && (
        <div className="mt-6 grid sm:grid-cols-4 gap-4">
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4" data-testid="avg-wage-card">
            <div className="text-xs text-emerald-700">平均工賃月額（B型）</div>
            <div className="mt-1 text-2xl font-bold text-emerald-900" data-testid="avg-wage-value">{yen(report.average_wage_b)}</div>
            <div className="text-xs text-emerald-600 mt-1">対象 {report.b_user_count} 名</div>
          </div>
          <div className="rounded-lg border border-stone-200 bg-white p-4">
            <div className="text-xs text-stone-500">B型工賃 合計</div>
            <div className="mt-1 text-xl font-semibold text-stone-900">{yen(report.b_total)}</div>
          </div>
          <div className="rounded-lg border border-stone-200 bg-white p-4">
            <div className="text-xs text-stone-500">A型賃金 合計</div>
            <div className="mt-1 text-xl font-semibold text-stone-900">{yen(report.a_total)}</div>
            <div className="text-xs text-stone-400 mt-1">{report.a_user_count} 名</div>
          </div>
          <div className="rounded-lg border border-stone-200 bg-white p-4">
            <div className="text-xs text-stone-500 flex items-center gap-1"><TrendingUp className="h-3 w-3" />直近平均工賃(B型)</div>
            <div className="mt-2 flex items-end gap-1 h-12" data-testid="wage-trend">
              {(report.trend || []).map((t) => {
                const max = Math.max(1, ...report.trend.map((x) => x.average_wage_b));
                return <div key={t.month} title={`${t.month}: ${yen(t.average_wage_b)}`} className="flex-1 bg-emerald-400 rounded-t" style={{ height: `${Math.max(4, (t.average_wage_b / max) * 100)}%` }} />;
              })}
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 rounded-lg border border-stone-200 bg-white p-5">
        <div className="font-medium text-stone-900 flex items-center gap-2"><Plus className="h-4 w-4" />作業記録を追加</div>
        <div className="mt-4 grid sm:grid-cols-4 gap-3">
          <div><Label className="text-sm">利用者名</Label><Input value={f.user_name} onChange={(e) => setF({ ...f, user_name: e.target.value })} data-testid="wage-user" className="mt-1.5" placeholder="利用者A" /></div>
          <div><Label className="text-sm">作業日</Label><Input type="date" value={f.work_date} onChange={(e) => setF({ ...f, work_date: e.target.value })} data-testid="wage-date" className="mt-1.5" /></div>
          <div><Label className="text-sm">区分</Label>
            <select value={f.wage_kind} onChange={(e) => setF({ ...f, wage_kind: e.target.value })} data-testid="wage-kind" className="mt-1.5 w-full rounded-md border border-stone-200 px-2 py-2 text-sm">
              <option value="B型工賃">B型工賃</option><option value="A型賃金">A型賃金</option>
            </select>
          </div>
          <div><Label className="text-sm">作業</Label>
            <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="wage-category" className="mt-1.5 w-full rounded-md border border-stone-200 px-2 py-2 text-sm">
              <option value="製造">製造</option><option value="販売">販売</option>
            </select>
          </div>
          <div><Label className="text-sm">単価</Label><Input type="number" value={f.unit_price} onChange={(e) => setF({ ...f, unit_price: e.target.value })} data-testid="wage-unit" className="mt-1.5" /></div>
          <div><Label className="text-sm">出来高</Label><Input type="number" value={f.output} onChange={(e) => setF({ ...f, output: e.target.value })} data-testid="wage-output" className="mt-1.5" /></div>
          <div><Label className="text-sm">支給額(空欄=単価×出来高)</Label><Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="wage-amount" className="mt-1.5" /></div>
          <div><Label className="text-sm">控除額</Label><Input type="number" value={f.deduction} onChange={(e) => setF({ ...f, deduction: e.target.value })} data-testid="wage-deduction" className="mt-1.5" /></div>
        </div>
        <button onClick={add} disabled={loading} data-testid="add-wage-button" className="mt-4 inline-flex items-center rounded-md bg-emerald-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-950 disabled:opacity-60">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "追加"}
        </button>
      </div>

      <div className="mt-6 rounded-lg border border-stone-200 bg-white overflow-hidden" data-testid="wages-list">
        <div className="grid grid-cols-6 gap-2 px-4 py-2 bg-stone-50 text-xs font-semibold text-stone-500">
          <span>作業日</span><span>利用者</span><span>区分</span><span className="text-right">支給額</span><span className="text-right">控除</span><span></span>
        </div>
        {rows.length === 0 && <div className="px-4 py-6 text-sm text-stone-500">この月の記録はありません。</div>}
        {rows.map((r) => (
          <div key={r.id} className="grid grid-cols-6 gap-2 px-4 py-2.5 text-sm items-center border-t border-stone-100">
            <span className="text-stone-600">{r.work_date}</span>
            <span className="text-stone-900">{r.user_name}</span>
            <span className="text-xs"><span className={`rounded-full px-2 py-0.5 ${r.wage_kind === "B型工賃" ? "bg-emerald-50 text-emerald-700" : "bg-blue-50 text-blue-700"}`}>{r.wage_kind}</span></span>
            <span className="text-right text-stone-900">{yen(r.amount)}</span>
            <span className="text-right text-stone-500">{yen(r.deduction)}</span>
            <span className="text-right"><button onClick={() => del(r.id)} data-testid={`wage-del-${r.id}`} className="text-stone-400 hover:text-orange-600"><Trash2 className="h-4 w-4" /></button></span>
          </div>
        ))}
      </div>
    </div>
  );
}
